import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  query,
  where,
  getDocs,
  doc,
  getDoc,
  collectionGroup,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { Business, BusinessMember } from '../types';

interface BusinessContextType {
  businesses: Business[];
  currentBusiness: Business | null;
  currentMembership: BusinessMember | null;
  loading: boolean;
  error: string | null;
  setCurrentBusiness: (business: Business | null) => void;
  refreshBusinesses: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export function BusinessProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
  const [currentMembership, setCurrentMembership] = useState<BusinessMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshBusinesses = async () => {
    if (!user) {
      setBusinesses([]);
      setCurrentBusiness(null);
      setCurrentMembership(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Find all memberships for this user
      const membershipsQuery = query(
        collectionGroup(db, 'members'),
        where('userId', '==', user.uid)
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);

      const businessList: Business[] = [];
      for (const memberDoc of membershipsSnapshot.docs) {
        const businessId = memberDoc.ref.parent.parent?.id;
        if (!businessId) continue;

        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        if (businessDoc.exists()) {
          businessList.push({
            id: businessDoc.id,
            ...businessDoc.data(),
          } as Business);
        }
      }

      setBusinesses(businessList);

      // Set first business as current if none selected
      if (businessList.length > 0 && !currentBusiness) {
        setCurrentBusiness(businessList[0]);
      }
    } catch (err) {
      console.error('Error fetching businesses:', err);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshBusinesses();
  }, [user]);

  // Update membership when current business changes
  useEffect(() => {
    const fetchMembership = async () => {
      if (!user || !currentBusiness) {
        setCurrentMembership(null);
        return;
      }

      try {
        const memberDoc = await getDoc(
          doc(db, 'businesses', currentBusiness.id, 'members', user.uid)
        );
        if (memberDoc.exists()) {
          setCurrentMembership(memberDoc.data() as BusinessMember);
        } else {
          setCurrentMembership(null);
        }
      } catch (err) {
        console.error('Error fetching membership:', err);
        setCurrentMembership(null);
      }
    };

    fetchMembership();
  }, [user, currentBusiness]);

  const value = {
    businesses,
    currentBusiness,
    currentMembership,
    loading,
    error,
    setCurrentBusiness,
    refreshBusinesses,
  };

  return (
    <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}
